import React, { createContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import DangerousIcon from "@mui/icons-material/Dangerous";
import BASE_URL from "../config/apiConfig";
import { showErrorToast } from "../util/showToast";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [initialLoad, setInitialLoad] = useState(true);

    const updateTrackerUser = (nextUser) => {
        if (typeof window === "undefined") return;
        const userId = nextUser?.email || nextUser?.username || nextUser?.userName || nextUser?.id;
        if (window.PATHWAYS_TRACKER_CONFIG) {
            window.PATHWAYS_TRACKER_CONFIG.userId = userId;
        }
        if (window.pathwaysTracker?.setUserId) {
            window.pathwaysTracker.setUserId(userId);
        }
    };

    const login = async (username, password, setLoading) => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/login`, { username: username.split("@")[0], password: password });
            const user = response.data;

            if (user) {
                setUser(user);
                updateTrackerUser(user);
            } else {
                showErrorToast("Incorrect username or password.", <DangerousIcon style={{ color: "#ef4337" }} />);
            }
        } catch (error) {
            console.error("Login failed: ", error);
            showErrorToast("Incorrect username or password.", <DangerousIcon style={{ color: "#ef4337" }} />);
        } finally {
            setLoading(false);
        }
    }

    const loginSSO = async (setLoading) => {
        setLoading(true);
        try {
            const res = await axios.get(`${BASE_URL}/api/check/me`, { withCredentials: true });
            const user = res.data;

            if (user) {
                setUser(user);
                updateTrackerUser(user);
            } else {
                showErrorToast("SSO login failed.", <DangerousIcon style={{ color: "#ef4337" }} />);
            }
        } catch (error) {
            console.error("SSO login failed:", error);
            showErrorToast("SSO login failed.", <DangerousIcon style={{ color: "#ef4337" }} />);
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/api/logout`, { withCredentials: true });
            if (response.status === 200) {
                setUser(null);
                updateTrackerUser(null);
            } else {
                showErrorToast("Logout failed. Please try again.", <DangerousIcon style={{ color: "#ef4337" }} />);
            }
        } catch (error) {
            console.error("Logout failed: ", error);
            showErrorToast("Logout failed.", <DangerousIcon style={{ color: "#ef4337" }} />);
        }
    }

    const checkAuthStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${BASE_URL}/api/check/me`, { withCredentials: true });
            const checkedUser = res.data || null;

            setUser(prevUser => {
                return (JSON.stringify(prevUser) !== JSON.stringify(checkedUser)) ? checkedUser : prevUser;
            });
            updateTrackerUser(checkedUser);
        } catch (err) {
            if (err.response?.status === 401 || err.response?.status === 403) {
                setUser(null);
                updateTrackerUser(null);
            }
        } finally {
            setInitialLoad(false);
        }
    }, []);

    useEffect(() => {
        checkAuthStatus();

        const interval = setInterval(() => {
            checkAuthStatus();
        }, 10 * 60 * 1000); // 10 minutes

        return () => clearInterval(interval);
    }, [checkAuthStatus]);

    return (
        <UserContext.Provider
            value={{
                user,
                initialLoad,
                login,
                loginSSO,
                logout,
                checkAuthStatus,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};
